import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { restartableTask, timeout } from 'ember-concurrency';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { isAppUpdateRequired } from 'garaje/utils/check-app-version';
import zft from 'garaje/utils/zero-for-tests';

import { type LocationOverviewVirtualFrontDeskLocationsIndexModel } from './route';

const MINIMUM_IPAD_VERSION_NEEDED_FOR_VFD = '4.27.0';
const DEBOUNCE_MS = 250;

export default class LocationOverviewVirtualFrontDeskLocationsIndexController extends Controller {
  declare model: LocationOverviewVirtualFrontDeskLocationsIndexModel;

  queryParams = ['sortBy', 'sortDirection', 'locationStatus', 'query'];

  @tracked sortBy?: string;
  @tracked sortDirection?: 'asc' | 'desc';
  @tracked locationStatus = 'all';
  @tracked query = '';

  @service declare featureFlags: FeatureFlagsService;
  @service declare state: StateService;

  minimumIpadVersionNeededForVfd = MINIMUM_IPAD_VERSION_NEEDED_FOR_VFD;

  @isAppUpdateRequired('model.loadIpadsTaskInstance.value', MINIMUM_IPAD_VERSION_NEEDED_FOR_VFD)
  isAppUpdateRequiredForVfd!: boolean;

  get enabledVfdCount(): number {
    return this.model.configurations.filter((config) => {
      // if visitors includes VFD, and visitors is enabled, we don't count it
      // eslint-disable-next-line ember/use-ember-get-and-set
      if (this.doesVisitorsIncludeVFD && config.location.get('visitorsEnabled')) return false;

      return config.enabled;
    }).length;
  }

  // TODO: quantity needs to be included in features API
  get vfdSubscriptionQty(): number {
    if (!this.state.features?.canAccessVirtualFrontDesk) return 0;

    return this.state.vfdSubscription?.quantity || 0;
  }

  get remainingInSubscription(): number {
    return Math.max(this.vfdSubscriptionQty - this.enabledVfdCount, 0);
  }

  get doesVisitorsIncludeVFD(): boolean {
    const visitorsSubscription = this.state.visitorsSubscription;
    if (!visitorsSubscription?.isUsable) return false;

    return visitorsSubscription.canAccessVirtualFrontDesk;
  }

  get hasVfdSubscription(): boolean {
    return !!this.state.vfdSubscription?.isSubscribed;
  }

  get hasQuantityGating(): boolean {
    return this.hasVfdSubscription && !this.onTrial;
  }

  get trialDaysLeft(): number | undefined {
    return this.state.vfdSubscription?.trialDaysLeft;
  }

  get onTrial(): boolean | undefined {
    return this.state.vfdSubscription?.onTrial;
  }

  searchTask = restartableTask(async (term: string) => {
    await timeout(zft(DEBOUNCE_MS));
    this.query = term;
  });

  @action
  handleSort(sortBy: string, sortDirection: 'asc' | 'desc'): void {
    this.sortBy = sortBy;
    this.sortDirection = sortDirection;
  }
}
